/**
 * A slider panel for the sky, the sea and the time of day.
 *
 * This exists because of how the last several days went. Every question about
 * the sky — is it too dark, too grey, too blown at the horizon — took a change,
 * a rebuild, a screenshot and a reply, and most of those round trips ended in
 * "a bit more hue" rather than a number. The values were always the problem and
 * they were never reachable while looking at the thing they controlled.
 *
 * So it edits `renderer.skyTune` in place. Those fields are read into the
 * uniform every frame, so a drag is visible on the next one — no rebuild, no
 * reload, nothing cached. COPY prints the current set as a TypeScript literal,
 * which is the whole point: tune by eye, then paste the answer into the
 * defaults and it ships.
 *
 * Deliberately plain DOM. The game's HUD is drawn into the canvas, and putting
 * sliders there would mean writing a widget toolkit in WGSL to answer a
 * question about constants.
 */
import type { Renderer } from '../render/renderer';
import { GUN_LIMIT } from '../game/chassis';
import { SEA } from '../game/config';

interface Row {
  key: string;
  label: string;
  min: number;
  max: number;
  step: number;
  /** Slider position -> value. Log for anything spanning orders of magnitude. */
  log?: boolean;
}

const SKY: Row[] = [
  { key: 'density', label: 'air density', min: 1e-5, max: 3e-4, step: 1e-6, log: true },
  { key: 'scaleHeight', label: 'scale height', min: 200, max: 9000, step: 50, log: true },
  { key: 'rayleigh', label: 'rayleigh', min: 0.2, max: 4, step: 0.01 },
  { key: 'mie', label: 'mie', min: 0, max: 1.5, step: 0.005 },
  { key: 'mieMul', label: 'mie strength', min: 0, max: 6, step: 0.05 },
  { key: 'mieG', label: 'mie g (halo tightness)', min: 0, max: 0.95, step: 0.01 },
  { key: 'odCap', label: 'optical depth cap', min: 4000, max: 120000, step: 500, log: true },
  { key: 'sunIntensity', label: 'sun intensity', min: 0, max: 12, step: 0.05 },
  // Neutral at zenith whatever the value — see `sunTint`. 0 is off.
  { key: 'sunset', label: 'sunset reddening (low sun)', min: 0, max: 30, step: 0.1 },
  // 0 = the whole sky warms, 1 = only the horizon does. See `sunsetGradient`.
  { key: 'sunsetGradient', label: 'sunset: horizon only (0 = whole sky)', min: 0, max: 1, step: 0.01 },
  { key: 'polarise', label: 'polariser', min: 0, max: 2, step: 0.01 },
  // OFFSETS FROM THE PRESET, not absolutes: zero is whatever the time of day
  // says, and all seven keep their relationship while a pair is being found.
  { key: 'sunAzimuth', label: 'sun azimuth \u00b1', min: -180, max: 180, step: 1 },
  { key: 'sunElevation', label: 'sun elevation \u00b1', min: -30, max: 30, step: 0.5 },
  { key: 'cloudAlt', label: 'cloud altitude', min: 400, max: 8000, step: 50 },
  { key: 'mistNear', label: 'mist near', min: 20, max: 800, step: 5 },
  { key: 'mistFar', label: 'mist far', min: 60, max: 3000, step: 10 },
];

const SEA_ROWS: Row[] = [
  // Art-safe ranges. The old maxima were diagnostic stress tests: foam 12 and
  // chop 9 did not describe rough water, they changed the screen into a white
  // or metallic material and made a small slider movement visually explosive.
  { key: 'foam', label: 'foam on crests', min: 0, max: 0.6, step: 0.01 },
  { key: 'frothScale', label: 'froth scale', min: 0.25, max: 2.4, step: 0.01 },
  { key: 'glint', label: 'sun glint', min: 0, max: 4, step: 0.02 },
  { key: 'reflection', label: 'reflection', min: 0, max: 6, step: 0.02 },
  { key: 'leaf', label: 'leaf translucency', min: 0, max: 8, step: 0.02 },
  { key: 'bioGlow', label: 'bioluminescence', min: 0, max: 6, step: 0.02 },
  { key: 'bioScatter', label: 'bio inscatter', min: 0, max: 0.6, step: 0.002 },
];

/**
 * The sea's SHAPE, which writes into `SEA` rather than into a render-only
 * block. That object is what `seaHeight` integrates the hull against, so moving
 * a slider here moves the water AND what floats on it — which is the only
 * honest way to expose these. A wave dial that changed the picture and not the
 * physics would be a lie you could see the moment the boat stopped bobbing.
 */
const WAVE: Row[] = [
  { key: 'k', label: 'wavenumber (small = long)', min: 0.10, max: 0.48, step: 0.002 },
  { key: 'amp', label: 'wave height', min: 0.25, max: 2.2, step: 0.02 },
  { key: 'speed', label: 'wave speed', min: 0.2, max: 1.6, step: 0.02 },
  { key: 'chop', label: 'chop (crest sharpness)', min: 1, max: 4.2, step: 0.02 },
  { key: 'fall', label: 'octave falloff', min: 0.24, max: 0.58, step: 0.005 },
  { key: 'mul', label: 'octave frequency step', min: 1.5, max: 2.15, step: 0.01 },
  { key: 'octaves', label: 'height octaves', min: 1, max: 3, step: 1 },
];

const DETAIL: Row[] = [
  { key: 'normalEps', label: 'detail size', min: 0.25, max: 1.4, step: 0.01 },
  { key: 'normalFlat', label: 'flatten distance', min: 18, max: 240, step: 1 },
  { key: 'detailOct', label: 'detail octaves', min: 1, max: 2, step: 1 },
  { key: 'tintDepth', label: 'tint depth', min: 0.5, max: 200, step: 0.5, log: true },
  { key: 'waveRelax', label: 'surface relaxation', min: 0, max: 1, step: 1 },
];

/**
 * ONE SUBSYSTEM EACH, AND INDEPENDENT.
 *
 * The F7 key steps a CUMULATIVE ladder, which answers "what is left after
 * turning off the first N things" — a question nobody asked. What is worth
 * knowing is what ONE thing costs while everything else runs normally, and
 * that is a row of switches, not a sequence.
 *
 * `get`/`set` rather than a key into an object, because these live in four
 * different places — quality tiers, the probe block, a renderer field and a
 * plain number — and a uniform interface here beats four special cases.
 */
interface Toggle {
  label: string;
  get(r: Renderer): boolean;
  set(r: Renderer, on: boolean): void;
}

/**
 * Two of these are numbers rather than flags — depth of field is an aperture
 * and the volumetric is a step fraction the F4 key cycles — so unchecking has
 * to remember what it found. Snapping them back to 1 would quietly re-tune the
 * thing under measurement, which is the one mistake an instrument cannot make.
 */
let dofOn = 0;
let volOn = 0;
let causticOn = 0;

const TOGGLES: Toggle[] = [
  { label: 'reflections (ssr)', get: (r) => r.gfxSsr > 0,
    set: (r, v) => { r.gfxSsr = v ? 3 : 0; } },
  { label: 'ambient occlusion', get: (r) => r.gfxAo > 0,
    set: (r, v) => { r.gfxAo = v ? 3 : 0; } },
  { label: 'contact shadows', get: (r) => r.gfxShadow > 0,
    set: (r, v) => { r.gfxShadow = v ? 3 : 0; } },
  { label: 'sky', get: (r) => r.gfxSky > 0,
    set: (r, v) => { r.gfxSky = v ? 3 : 0; } },
  { label: 'sea surface', get: (r) => r.seaSurface,
    set: (r, v) => { r.seaSurface = v; } },
  { label: '  • underwater IBL reflection (old colour)', get: (r) => r.probe.surfaceSky,
    set: (r, v) => { r.probe.surfaceSky = v; } },
  { label: '  • refract submerged scene (slower / can seam)', get: (r) => r.probe.surfaceRefraction,
    set: (r, v) => { r.probe.surfaceRefraction = v; } },
  { label: '  • legacy micro ripples + clouds (slower)', get: (r) => r.probe.surfaceMicro,
    set: (r, v) => { r.probe.surfaceMicro = v; } },
  { label: '  • folded legacy wave profile', get: () => SEA.legacyProfile,
    set: (_r, v) => { SEA.legacyProfile = v; } },
  { label: 'TAA depth-edge clamp (can outline water)', get: (r) => r.probe.taaDepthClamp,
    set: (r, v) => { r.probe.taaDepthClamp = v; } },
  { label: 'wake  (23 passes)', get: (r) => r.probe.wake,
    set: (r, v) => { r.probe.wake = v; } },
  { label: 'particles', get: (r) => r.probe.particles,
    set: (r, v) => { r.probe.particles = v; } },
  // Per KIND, so the field can be bisected instead of argued about. Culled in
  // the vertex stage, so each of these is also a fair cost measurement.
  { label: '  • silt haze (87% of 24k)', get: (r) => r.probe.pKind.ambient,
    set: (r, v) => { r.probe.pKind.ambient = v; } },
  { label: '  • plankton (bright specks)', get: (r) => r.probe.pKind.plankton,
    set: (r, v) => { r.probe.pKind.plankton = v; } },
  { label: '  • bubbles (wake, trails)', get: (r) => r.probe.pKind.bubble,
    set: (r, v) => { r.probe.pKind.bubble = v; } },
  { label: '  • sparks + embers', get: (r) => r.probe.pKind.spark,
    set: (r, v) => { r.probe.pKind.spark = v; } },
  { label: '  • debris (explosions)', get: (r) => r.probe.pKind.debris,
    set: (r, v) => { r.probe.pKind.debris = v; } },
  { label: '  • clouds (sediment, ink, bio)', get: (r) => r.probe.pKind.cloud,
    set: (r, v) => { r.probe.pKind.cloud = v; } },
  { label: '  • surface spray (breaches)', get: (r) => r.probe.pKind.spray,
    set: (r, v) => { r.probe.pKind.spray = v; } },
  { label: 'depth of field', get: (r) => r.dof > 0,
    set: (r, v) => { if (!v) { dofOn = r.dof || dofOn; r.dof = 0; }
                     else { r.dof = dofOn || 1; } } },
  { label: 'bloom', get: (r) => r.probe.bloom,
    set: (r, v) => { r.probe.bloom = v; } },
  { label: 'volumetric light', get: (r) => r.probe.volumetric > 0.001,
    set: (r, v) => { if (!v) { volOn = r.probe.volumetric || volOn; r.probe.volumetric = 0; }
                     else { r.probe.volumetric = volOn || 1; } } },
  { label: 'point lights', get: (r) => r.probe.lights,
    set: (r, v) => { r.probe.lights = v; } },
  // Its own strength IS its switch — the composite gates the whole block on
  // `U.grade.z`, so zeroing it costs nothing to test and the slider two
  // sections up is the same number.
  { label: 'caustics', get: (r) => r.seaTune.caustic > 0.0001,
    set: (r, v) => { if (!v) { causticOn = r.seaTune.caustic || causticOn;
                               r.seaTune.caustic = 0; }
                     else { r.seaTune.caustic = causticOn || 0.42; } } },
  { label: 'terrain', get: (r) => r.probe.terrain,
    set: (r, v) => { r.probe.terrain = v; } },
  // The splat pass itself, apart from the strength. Unchecking wipes the map
  // rather than freezing it: a switched-off effect that leaves its last frame
  // painted on the seabed is worse than one that is simply absent.
  { label: 'caustic splat pass', get: (r) => r.probe.caustics,
    set: (r, v) => { r.probe.caustics = v; } },
  // Asked for as a standing rule, and right: a feature with no switch cannot be
  // priced and cannot be ruled out when something else looks wrong.
  { label: 'height map (top-down)', get: (r) => r.probe.heightMap,
    set: (r, v) => { r.probe.heightMap = v; } },
  { label: 'beam phase (forward scatter)', get: (r) => r.probe.phase,
    set: (r, v) => { r.probe.phase = v; } },
  { label: 'terrain sun shadows', get: (r) => r.probe.sunShadow,
    set: (r, v) => { r.probe.sunShadow = v; } },
  { label: 'shaft core (hard middle)', get: (r) => r.probe.shaftCore,
    set: (r, v) => { r.probe.shaftCore = v; } },
  { label: 'shaft grain (along its length)', get: (r) => r.probe.shaftGrain,
    set: (r, v) => { r.probe.shaftGrain = v; } },
  { label: 'caustic window leads the sun', get: (r) => r.probe.shaftLead,
    set: (r, v) => { r.probe.shaftLead = v; } },
  { label: 'motes lit by sun shafts', get: (r) => r.probe.moteSun,
    set: (r, v) => { r.probe.moteSun = v; } },
  { label: 'ambient occluded from above', get: (r) => r.probe.skyAbove,
    set: (r, v) => { r.probe.skyAbove = v; } },
  { label: 'reference-art detail (rock + terrain)', get: (r) => r.probe.artDetail,
    set: (r, v) => { r.probe.artDetail = v; } },
  { label: 'studio reflections (off-screen)', get: (r) => r.probe.studio,
    set: (r, v) => { r.probe.studio = v; } },
  // Off collapses the scene back to ONE render pass — this removes the cost,
  // not just the effect. See `scene:opaque` in renderer.ts.
  { label: 'refraction (glass, jellies)', get: (r) => r.probe.refraction,
    set: (r, v) => { r.probe.refraction = v; } },
  { label: 'bounded backdrop copy (off = original full frame)',
    get: (r) => r.probe.backdropBounds,
    set: (r, v) => { r.probe.backdropBounds = v; } },
  { label: 'DOF bleed guard (sharp things stay put)', get: (r) => r.probe.dofGuard,
    set: (r, v) => { r.probe.dofGuard = v; } },
  { label: 'motes write g-buffer (old, buggy)', get: (r) => r.probe.moteGbuf,
    set: (r, v) => { r.probe.moteGbuf = v; } },
  { label: 'metal lit by ambient (not just the lamp)', get: (r) => r.probe.metalAmbient,
    set: (r, v) => { r.probe.metalAmbient = v; } },
  { label: 'ore crystal shading', get: (r) => r.probe.crystal,
    set: (r, v) => { r.probe.crystal = v; } },
  { label: 'gem glass (dark body, bright edges)', get: (r) => r.probe.gemGlass,
    set: (r, v) => { r.probe.gemGlass = v; } },
  { label: 'glass transmission (see through windows)', get: (r) => r.probe.glassTransmit,
    set: (r, v) => { r.probe.glassTransmit = v; } },
  { label: 'triplanar detail (surface grain on every face)', get: (r) => r.probe.triplanar,
    set: (r, v) => { r.probe.triplanar = v; } },
  { label: 'surface wear (roughness from the detail set)', get: (r) => r.probe.surfaceWear,
    set: (r, v) => { r.probe.surfaceWear = v; } },
  { label: 'split-sum DFG + energy compensation', get: (r) => r.probe.dfg,
    set: (r, v) => { r.probe.dfg = v; } },
  { label: 'specular AA (fixes blocky mirrors below native res)', get: (r) => r.probe.specAA,
    set: (r, v) => { r.probe.specAA = v; } },
  { label: 'surface grain on built materials', get: (r) => r.probe.builtGrain,
    set: (r, v) => { r.probe.builtGrain = v; } },
  { label: 'colour bleed (one bounce)', get: (r) => r.probe.ssgi,
    set: (r, v) => { r.probe.ssgi = v; } },
  { label: 'cabins lit as air (not water)', get: (r) => r.probe.cabinAir,
    set: (r, v) => { r.probe.cabinAir = v; } },
  { label: '  \u2022 refract jellies too (costs backdrop area)', get: (r) => r.probe.jellyRefraction,
    set: (r, v) => { r.probe.jellyRefraction = v; } },
  /**
   * A PRESET, NOT A GATE. It writes the three sliders below rather than
   * switching them on, so they are live whether it is ticked or not — the first
   * version gated them, and dragging one with the box unticked did nothing at
   * all, which reads as broken rather than as off.
   */
  { label: 'physical lamp falloff (writes the 3 below)', get: (r) => r.probe.physicalLamp,
    set: (r, v) => {
      r.probe.physicalLamp = v;
      const t = r.seaTune;
      if (v) { t.lampScale = 0.14; t.lampLinear = 0.05; t.lampQuadratic = 0.015; } else {
        t.lampScale = 1.0; t.lampLinear = 0.055; t.lampQuadratic = 0.0030;
      }
    } },
  { label: 'water haze (inscatter)', get: (r) => r.probe.haze,
    set: (r, v) => { r.probe.haze = v; } },
  { label: 'sunset (atmosphere reddens a low sun)', get: (r) => r.probe.sunset,
    set: (r, v) => { r.probe.sunset = v; } },
  // Off by default and worth switching off again. See `gpuPassTiming`: forty
  // timestamp writes a frame is a measurement that changes what it measures,
  // especially on the tiled mobile GPUs this exists to profile.
  { label: 'GPU per-pass timing (F3, costs a little)', get: (r) => r.gpuPassTiming,
    set: (r, v) => { r.gpuPassTiming = v; } },
  // NOT A SETTING — a one-shot, shaped as a toggle because that is the only
  // control this panel has. Ticking it restores every mined-out seam and
  // unticks itself, so it reads as a button and cannot be left on by accident.
  { label: 'restore all mined seams (one shot)', get: () => false,
    set: (r, v) => { if (v) r.probe.resetOre = true; } },
  { label: 'ore point lights (one per glowing node)', get: (r) => r.probe.oreLight,
    set: (r, v) => { r.probe.oreLight = v; } },
  { label: 'ore self-colour (metal without the lamp)', get: (r) => r.probe.oreSheen,
    set: (r, v) => { r.probe.oreSheen = v; } },
  { label: 'text: SDF coverage (off = bitmap atlas)', get: (r) => r.probe.textSdf,
    set: (r, v) => { r.probe.textSdf = v; } },
  { label: 'mesh draws', get: (r) => r.probe.instances,
    set: (r, v) => { r.probe.instances = v; } },
  // The world freezes with this off — the buffers keep their last contents —
  // and everything else carries on being drawn at full cost. Which is the
  // point: the difference is the upload and nothing but the upload.
  { label: 'instance upload  (freezes)', get: (r) => r.probe.instanceUpload,
    set: (r, v) => { r.probe.instanceUpload = v; } },
  // The overlay. A second canvas the browser composites over the render, so
  // nothing inside the renderer can see it — and with everything else off it
  // is a real fraction of what is left.
  { label: 'hud overlay', get: (r) => r.probe.hud,
    set: (r, v) => { r.probe.hud = v; } },
];

const RENDER: Row[] = [
  // Megapixels, because that is the quantity the cost is proportional to —
  // a percentage hides the fact that 70 percent of the width is half the work.
  { key: 'pixels', label: 'render megapixels', min: 0.3, max: 3.2, step: 0.05 },
  { key: 'hudPixels', label: 'hud megapixels', min: 0.5, max: 8.5, step: 0.1 },
];

const GUN: Row[] = [
  // Degrees, and asymmetric on purpose — up is where the housing fouls the
  // pedestal, down is where the shot clips the bow. See GUN_LIMIT in
  // chassis.ts for where the two numbers came from.
  { key: 'up', label: 'gun elevation (deg)', min: 0, max: 60, step: 1 },
  { key: 'down', label: 'gun depression (deg)', min: 0, max: 60, step: 1 },
];

/**
 * THE BOAT. Everything that only ever moves the submarine, in one place.
 *
 * These were scattered through `CAUSTIC`, which had become the drawer anything
 * without an obvious home went into — a hull studio light and a lamp occlusion
 * term filed under caustics. They read from `seaTune` like the rest, so the
 * COPY button below picks them up unchanged.
 */
const SUB: Row[] = [
  // See `subGlassW` in scene.wgsl. 0 leaves the viewports as lit plates, which
  // is what they have always been; 1 cuts their diffuse to a tenth so the
  // Fresnel rim carries them and they read as glass. The running lights share
  // the tag and are untouched either way — emission is added elsewhere.
  { key: 'subGlass', label: 'viewports as glass (0 = lit plate)', min: 0, max: 1, step: 0.01 },
  // The hull's own cylindrical unwrap, which is what sets how big a plate is.
  // Only the boat has an unwrap; everything else is projected triplanar.
  { key: 'subPanel', label: 'hull plate size (unwrap scale)', min: 0.15, max: 4, step: 0.05 },
  { key: 'subStudio', label: 'hull studio light (refit panel)', min: 0, max: 2.5, step: 0.01 },
  { key: 'standEmissive', label: 'refit panel emission', min: 0, max: 3, step: 0.01 },
  // 0 = the boat lit by the water like anything else, 1 = the rig as shipped.
  { key: 'studioRig', label: 'sub studio rig', min: 0, max: 2, step: 0.01 },
  // 0 = lamps ignore the hull's own creases, 1 = they are occluded as fully as
  // the sky is. The keel shadow lives in here.
  { key: 'lampAo', label: 'lamp occlusion', min: 0, max: 1, step: 0.01 },
  // Always live. The checkbox above writes them; it does not gate them.
  { key: 'lampScale', label: '  \u2022 lamp intensity x (of 12)', min: 0.01, max: 1.5, step: 0.01 },
  { key: 'lampLinear', label: '  \u2022 lamp falloff linear', min: 0, max: 0.4, step: 0.005 },
  { key: 'lampQuadratic', label: '  \u2022 lamp falloff quadratic', min: 0, max: 0.08, step: 0.001 },
];

/** Toggles that only ever move the submarine. */
const SUB_TOGGLES: Toggle[] = [
  { label: 'viewports shaded as glass', get: (r) => r.probe.subGlass,
    set: (r, v) => { r.probe.subGlass = v; } },
  // The boat is the only asset whose paint lands on an UNWRAP rather than a
  // triplanar projection, so the panel-seam map's pitch is set by `hullUv` and
  // is worth being able to A/B against the plating scan it replaced.
  { label: 'hull paint: panel seams (off = plating scan)', get: (r) => r.probe.subPlate,
    set: (r, v) => { r.probe.subPlate = v; } },
  { label: 'studio key shadow (hero)', get: (r) => r.probe.rigShadow,
    set: (r, v) => { r.probe.rigShadow = v; } },
  { label: 'refit panel keeps own light (emission)', get: (r) => r.probe.standEmissive,
    set: (r, v) => { r.probe.standEmissive = v; } },
];

const CAUSTIC: Row[] = [
  { key: 'caustic', label: 'strength', min: 0, max: 20, step: 0.05 },
  // Reused rather than retired. It scaled the old noise pattern's frequency;
  // it is now how far below the surface the splat projects, which sets both the
  // filament size and how fast they travel — the same quantity twice, since
  // both are the lateral throw of a ray and that grows with the distance flown.
  { key: 'causticSize', label: 'focus depth (m)', min: 0.5, max: 40, step: 0.1 },
  { key: 'causticReach', label: 'depth reach', min: 5, max: 900, step: 5, log: true },
  // The two shaft refinements. Both 0 = the beam exactly as it was before them.
  { key: 'shaftCore', label: 'shaft core', min: 0, max: 2, step: 0.01 },
  { key: 'shaftGrain', label: 'shaft grain', min: 0, max: 1, step: 0.01 },
  { key: 'shaftLead', label: 'window lead (up-sun)', min: 0, max: 1, step: 0.01 },
  { key: 'moteSun', label: 'motes in shafts', min: 0, max: 4, step: 0.01 },
  { key: 'skyAbove', label: 'overhang shadow', min: 0, max: 1, step: 0.01 },
  // 1 is what shipped, and what tiles the coral-polyp render across every
  // rock as recognisable circles. See the note in `fsTerrain`.
  { key: 'artDetail', label: 'art detail amount', min: 0, max: 1.5, step: 0.01 },
  { key: 'studio', label: 'studio reflection', min: 0, max: 3, step: 0.01 },
  { key: 'refraction', label: 'refraction', min: 0, max: 2, step: 0.01 },
  // --- how far light gets, in both directions ------------------------------
  // 1 on all four is the game exactly as authored. `depthFall` warps the depth
  // the band table is read at, `zFall` scales the water between the lens and a
  // surface, `density` thickens the water itself and `laneTint` is the flat
  // multiply the parallax lanes take on top of all of it.
  { key: 'depthFall', label: 'depth falloff (y)', min: 0, max: 3, step: 0.01 },
  { key: 'zFall', label: 'distance falloff (z)', min: 0, max: 3, step: 0.01 },
  { key: 'density', label: 'water density', min: 0, max: 3, step: 0.01 },
  { key: 'laneTint', label: 'lane tint', min: 0, max: 2, step: 0.01 },
  // 1 is the physically correct amount, not a taste setting — see `ambientSpec`.
  { key: 'metalAmbient', label: 'metal ambient (specular)', min: 0, max: 3, step: 0.01 },
  { key: 'oreSheen', label: 'ore self-colour', min: 0, max: 2, step: 0.01 },
  { key: 'haze', label: 'water haze (1 = as authored)', min: 0, max: 1.6, step: 0.01 },
  { key: 'oreGlow', label: 'ore glow (authored emission)', min: 0, max: 4, step: 0.01 },
  { key: 'oreTranslucency', label: 'ore translucency', min: 0, max: 3, step: 0.01 },
  { key: 'gemDiffuse', label: 'gem diffuse (low = glass)', min: 0, max: 1, step: 0.01 },
  { key: 'surfaceWear', label: 'surface wear (roughness break-up)', min: 0, max: 2, step: 0.05 },
  { key: 'glassBody', label: 'glass body (0 = invisible pane)', min: 0, max: 0.6, step: 0.01 },
  { key: 'glassRefract', label: 'glass refraction (lensing)', min: 0, max: 4, step: 0.05 },
  { key: 'specAA', label: 'specular AA strength', min: 0, max: 4, step: 0.05 },
  { key: 'builtDetail', label: 'grain size on built surfaces', min: 0.1, max: 4, step: 0.05 },
  { key: 'builtGrain', label: 'grain strength on built (0 = spotless)', min: 0, max: 1.5, step: 0.01 },
  { key: 'ssgi', label: 'colour bleed (one bounce)', min: 0, max: 4, step: 0.05 },
  { key: 'cabinAmbient', label: 'cabin ambient (1 = flooded)', min: 0, max: 1, step: 0.01 },
  { key: 'oreLight', label: 'ore point light', min: 0, max: 4, step: 0.01 },
  { key: 'crystalScale', label: 'crystal size (gems)', min: 0.2, max: 4, step: 0.01 },
  { key: 'metalOreScale', label: 'metal ore size', min: 0.2, max: 4, step: 0.01 },
  // 0 is off: the stone falls back to whatever the sea is doing at the station,
  // which is what it did before. Above 1.02 it also gets the studio room to
  // reflect — see `oreStudio`, where that threshold comes from.
  { key: 'oreStudio', label: 'cargo studio light (refit panel)', min: 0, max: 2.5, step: 0.01 },
  // Undersides only. Was chained to `strength`, which is why that slider
  // appeared to put caustics on the bottoms of rocks.
  { key: 'bounce', label: 'seabed bounce (undersides)', min: 0, max: 0.5, step: 0.005 },
  // Not a caustic, but it shares tune4 and it is the other half of the light
  // the corrected normal moved: what a missed reflection gets from the probe.
  // Wider, because it now drives the FORWARD pass as well as the screen-space
  // fallback, and 0.16 was a number chosen while it was reaching a quarter of
  // what it named.
  { key: 'envGain', label: 'env reflection', min: 0, max: 4, step: 0.01 },
  // 0 = a point light stops dead at the terminator, 0.3 = the old flat wash.
  { key: 'pointWrap', label: 'point light wrap', min: 0, max: 0.5, step: 0.01 },
  // 0 = the key light passes through the boat, 1 = the deck shadows the keel
  // as hard as the height map can put it.
  { key: 'rigShadow', label: 'studio key shadow', min: 0, max: 1, step: 0.01 },
];

const CSS = `
.sky-tune{position:fixed;top:8px;right:8px;width:270px;z-index:40;
  font:11px/1.45 ui-monospace,SFMono-Regular,Menlo,monospace;color:#cfe9f2;
  background:rgba(6,18,26,.88);border:1px solid rgba(120,200,220,.28);
  border-radius:6px;padding:8px 10px 10px;backdrop-filter:blur(6px);
  max-height:calc(100vh - 16px);overflow:auto}
.sky-tune h4{margin:6px 0 4px;font-size:10px;letter-spacing:.14em;
  text-transform:uppercase;color:#7fd4e8;font-weight:600}
.sky-tune .r{display:grid;grid-template-columns:1fr 62px;gap:4px;align-items:center;
  margin:1px 0}
.sky-tune label{opacity:.8;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.sky-tune input[type=range]{grid-column:1/3;width:100%;height:12px;margin:0}
.sky-tune .v{text-align:right;opacity:.95;font-variant-numeric:tabular-nums}
.sky-tune .t{display:grid;grid-template-columns:16px 1fr;gap:6px;align-items:center;
  margin:2px 0;cursor:pointer}
.sky-tune .t input{width:14px;height:14px;margin:0;accent-color:#5fe4e0;cursor:pointer}
.sky-tune .t span{opacity:.85;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.sky-tune button{font:inherit;color:#cfe9f2;background:rgba(120,200,220,.14);
  border:1px solid rgba(120,200,220,.3);border-radius:4px;padding:3px 8px;
  cursor:pointer;margin:6px 4px 0 0}
.sky-tune button:hover{background:rgba(120,200,220,.26)}
.sky-tune select{font:inherit;width:100%;color:#cfe9f2;background:rgba(6,18,26,.9);
  border:1px solid rgba(120,200,220,.3);border-radius:4px;padding:2px}
.sky-tune .hint{opacity:.5;margin-top:6px;font-size:10px}
`;

/** Human-readable, and short enough to sit in a 62 px column. */
function fmt(v: number): string {
  if (v === 0) return '0';
  const a = Math.abs(v);
  if (a < 0.001) return v.toExponential(1);
  if (a < 1) return v.toFixed(3);
  if (a < 100) return v.toFixed(2);
  return Math.round(v).toString();
}

export class SkyTuner {
  private root: HTMLDivElement | null = null;
  private syncs: (() => void)[] = [];

  constructor(
    private readonly renderer: Renderer,
    private readonly times: readonly { id: string; name: string }[],
    private readonly onTime: (id: string) => void,
    private readonly currentTime: () => string,
  ) {}

  get open(): boolean { return this.root !== null; }

  toggle() { if (this.root) this.close(); else this.show(); }

  close() {
    this.root?.remove();
    this.root = null;
    this.syncs = [];
  }

  /** Pull the widgets back in line with the values, after a preset load. */
  refresh() { for (const s of this.syncs) s(); }

  private show() {
    if (!document.getElementById('sky-tune-css')) {
      const st = document.createElement('style');
      st.id = 'sky-tune-css';
      st.textContent = CSS;
      document.head.appendChild(st);
    }
    const root = document.createElement('div');
    root.className = 'sky-tune';
    // Claims wheel and keys for itself — see the guard in `Input.wheel`, and
    // the arrow keys below, which would otherwise drive a slider and the menu
    // selection at the same time.
    root.dataset.overlay = 'sky-tune';
    root.addEventListener('keydown', (e) => e.stopPropagation());
    root.addEventListener('wheel', (e) => e.stopPropagation(), { passive: true });
    this.root = root;

    // Time of day first: every other number here means something different
    // under a different sun, so the preset is part of the instrument.
    const h0 = document.createElement('h4');
    h0.textContent = 'time of day';
    root.appendChild(h0);
    const sel = document.createElement('select');
    for (const t of this.times) {
      const o = document.createElement('option');
      o.value = t.id;
      o.textContent = t.name;
      sel.appendChild(o);
    }
    sel.value = this.currentTime();
    sel.onchange = () => this.onTime(sel.value);
    root.appendChild(sel);

    const h1 = document.createElement('h4');
    h1.textContent = 'sky';
    root.appendChild(h1);

    const tune = this.renderer.skyTune as unknown as Record<string, number>;
    for (const row of SKY) root.appendChild(this.slider(row, tune));

    root.appendChild(this.slider(
      { key: 'cloudCover', label: 'cloud cover', min: 0, max: 1, step: 0.01 },
      this.renderer as unknown as Record<string, number>,
    ));

    const sea = this.renderer.seaTune as unknown as Record<string, number>;
    const h2 = document.createElement('h4');
    h2.textContent = 'water surface';
    root.appendChild(h2);
    for (const row of SEA_ROWS) root.appendChild(this.slider(row, sea));

    const hw = document.createElement('h4');
    hw.textContent = 'waves  (moves the physics too)';
    root.appendChild(hw);
    const wave = SEA as unknown as Record<string, number>;
    for (const row of WAVE) root.appendChild(this.slider(row, wave));

    const hd = document.createElement('h4');
    hd.textContent = 'surface detail';
    root.appendChild(hd);
    for (const row of DETAIL) root.appendChild(this.slider(row, sea));

    const hr = document.createElement('h4');
    hr.textContent = 'render';
    root.appendChild(hr);
    for (const row of RENDER) root.appendChild(this.slider(row, tune));
    // Straight at the module constant. No plumbing: `Player` reads it every
    // frame, so a drag here moves the stops on the boat that is already flying.
    const hg = document.createElement('h4');
    hg.textContent = 'gun mount';
    root.appendChild(hg);
    for (const row of GUN) root.appendChild(this.slider(row, GUN_LIMIT));

    const hs = document.createElement('h4');
    hs.textContent = 'sub';
    root.appendChild(hs);
    for (const row of SUB) root.appendChild(this.slider(row, sea));
    for (const t of SUB_TOGGLES) root.appendChild(this.checkbox(t));

    const hx = document.createElement('h4');
    hx.textContent = 'subsystems  (uncheck to price)';
    root.appendChild(hx);
    for (const t of TOGGLES) root.appendChild(this.checkbox(t));

    const h3 = document.createElement('h4');
    h3.textContent = 'caustics';
    root.appendChild(h3);
    for (const row of CAUSTIC) root.appendChild(this.slider(row, sea));

    const copy = document.createElement('button');
    copy.textContent = 'COPY';
    copy.onclick = () => {
      const one = (rows: Row[], t: Record<string, number>) =>
        rows.map((r) => `    ${r.key}: ${Number(t[r.key]!.toPrecision(4))},`).join('\n');
      const text = `skyTune = {\n${one(SKY, tune)}\n  };\n\n`
        + `seaTune = {\n${one([...SEA_ROWS, ...DETAIL, ...SUB, ...CAUSTIC], sea)}\n  };\n\n`
        + `SEA = {\n${one(WAVE, wave)}\n  };\n\n`
        + `cloudCover = ${Number(this.renderer.cloudCover.toPrecision(3))};`;
      void navigator.clipboard?.writeText(text);
      copy.textContent = 'COPIED';
      setTimeout(() => { copy.textContent = 'COPY'; }, 900);
    };
    root.appendChild(copy);

    const close = document.createElement('button');
    close.textContent = 'CLOSE';
    close.onclick = () => this.close();
    root.appendChild(close);

    const hint = document.createElement('div');
    hint.className = 'hint';
    hint.textContent = 'K toggles. Changes are live; COPY gives you the literal.';
    root.appendChild(hint);

    document.body.appendChild(root);
  }

  /** Not `toggle` — that name is already the panel's own show/hide. */
  private checkbox(t: Toggle): HTMLLabelElement {
    const wrap = document.createElement('label');
    wrap.className = 't';
    const inp = document.createElement('input');
    inp.type = 'checkbox';
    const lab = document.createElement('span');
    lab.textContent = t.label;
    const sync = () => { inp.checked = t.get(this.renderer); };
    sync();
    this.syncs.push(sync);
    inp.onchange = () => {
      t.set(this.renderer, inp.checked);
      // A toggle is allowed to write tune VALUES, not just its own flag — the
      // lamp preset does. Re-syncing every row afterwards keeps the sliders
      // showing what is actually in effect instead of what was there before.
      this.refresh();
    };
    wrap.append(inp, lab);
    return wrap;
  }

  private slider(row: Row, target: Record<string, number>): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.className = 'r';
    const lab = document.createElement('label');
    lab.textContent = row.label;
    const val = document.createElement('span');
    val.className = 'v';
    const inp = document.createElement('input');
    inp.type = 'range';

    // A LOG SLIDER WHERE THE RANGE DEMANDS ONE. Density runs from 1e-5 to 3e-4;
    // on a linear track every useful value is inside the first eighth of the
    // travel and the control is unusable exactly where it matters.
    const toPos = (v: number) => row.log
      ? (Math.log(v) - Math.log(row.min)) / (Math.log(row.max) - Math.log(row.min))
      : (v - row.min) / (row.max - row.min);
    const toVal = (p: number) => row.log
      ? Math.exp(Math.log(row.min) + p * (Math.log(row.max) - Math.log(row.min)))
      : row.min + p * (row.max - row.min);

    inp.min = '0';
    inp.max = '1000';
    inp.step = '1';
    const sync = () => {
      inp.value = String(Math.round(toPos(target[row.key]!) * 1000));
      val.textContent = fmt(target[row.key]!);
    };
    sync();
    this.syncs.push(sync);
    inp.oninput = () => {
      const v = toVal(Number(inp.value) / 1000);
      target[row.key] = row.step >= 1 ? Math.round(v / row.step) * row.step : v;
      val.textContent = fmt(target[row.key]!);
    };
    wrap.append(lab, val, inp);
    return wrap;
  }
}
